<template>
  <div class="selected-slide">
    <div class="selected-slide__container" @click="handleEmitOnClose">
      <IconsBulkSelect class="transform scale-95" />
      <h3 class="selected-slide__title" data-testid="default-export-title">
        {{ slides.length }} {{ pluralize(slides.length, 'Slide', 'Slides') }} Selected
      </h3>
      <div v-if="slides.length > 1" ref="slideButtons" class="selected-slide__content">
        <EditorViewerButton
          v-if="allThreeDSlides"
          data-testid="default-title-visibility-btn"
          @click="$emit('update-title-visibility', slides)"
        >
          <EyeSlashIcon v-if="someThreeDTitlesVisible" class="transform scale-95 h-9 w-9" />
          <EyeIcon v-else class="transform scale-95 h-9 w-9" />
          <span>{{ someThreeDTitlesVisible ? 'Hide' : 'Show' }} 3D Slide Titles</span>
        </EditorViewerButton>
        <EditorViewerButton data-testid="default-export-btn" @click="$emit('on-export', slides)">
          <IconsExportIcon class="transform scale-95" />
          <span>Export to another Deck</span>
        </EditorViewerButton>
        <EditorViewerButton v-if="allPdfSlides" data-testid="default-replace-pdf-btn" @click="$emit('on-replace', slides)">
          <FontAwesomeIcon icon="repeat" size="3x" />
          <span>Bulk Replace PDF Source</span>
        </EditorViewerButton>
        <EditorViewerButton data-testid="default-delete-btn" @click="$emit('on-delete', slides)">
          <IconsTrashIcon class="transform scale-95" />
          <span>Delete</span>
        </EditorViewerButton>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { PropType, DeepReadonly } from 'vue'
import type { Slide } from '@/entities/deck'
import { pluralize } from '@/lib/pluralize'
import { isChildOf } from '@/lib/utils'
import { EyeIcon, EyeSlashIcon } from '@heroicons/vue/24/solid'
import IconsBulkSelect from '@/components/icons/BulkSelect.svg'
import IconsExportIcon from '@/components/icons/ExportIcon.svg'
import IconsTrashIcon from '@/components/icons/TrashIcon.svg'

const props = defineProps({
  slides: {
    type: Array as PropType<DeepReadonly<Slide[]>>,
    required: true,
  },
})

const emit = defineEmits(['on-export', 'on-delete', 'on-close', 'update-title-visibility', 'on-replace'])

const slideButtons = ref<HTMLElement | undefined>()

function handleEmitOnClose(e: MouseEvent) {
  if (isChildOf(e.target as Node, slideButtons.value)) {
    return
  }

  emit('on-close')
}

const allPdfSlides = computed(() => !props.slides.some(slide => slide.type !== 'P'))
const allThreeDSlides = computed(() => !props.slides.some(slide => slide.type !== 'M'))
const someThreeDTitlesVisible = computed(() => props.slides.some(slide => slide.title_visible_in_3d_model))
</script>

<style lang="stylus">
.selected-slide
  @apply flex flex-col h-full w-full relative text-center bg-gray-900 text-white

  &__container
    @apply flex flex-col flex-grow justify-center items-center
  &__title
    @apply font-normal text-3xl sm_text-5xl mt-8 mb-12
  &__content
    @apply flex gap-6
</style>
